// Pure helpers for the customer "Screen health" pages. No React, no i18n instance: callers pass `t`.
// The backend sends i18n KEYS + params (never prose); this file turns them into the plain sentences.

// One truth: the backend sends ONE customer state derived from the same presence function the Screens list uses.
// working / needs_you / offline / standby / app_closed / limited. Classes below are the merged prototype's tones.
export const STATE_UI = {
  working: { tone: 'g', badge: 'on' },
  needs_you: { tone: 'c', badge: 'need' },
  offline: { tone: 'c', badge: 'off' },
  standby: { tone: 'w', badge: 'standby' },
  // build 396: a deliberate pause is shown EXACTLY like standby (amber)
  paused: { tone: 'w', badge: 'standby' },
  // build 397: a suspected-but-unconfirmed sound problem -- amber, never green
  checking: { tone: 'w', badge: 'standby' },
  app_closed: { tone: 'b', badge: 'closed' },
  limited: { tone: 'n', badge: 'limited' },
};
export const stateUi = (state) => STATE_UI[state] || STATE_UI.limited;
/** Screens that need the owner (the "Need you" banner/filter): a health fault, offline, or the app closed. */
export const NEEDS_OWNER = new Set(['needs_you', 'offline', 'app_closed']);
export const needsOwner = (state) => NEEDS_OWNER.has(state);
export const ACTION_TONE = { green: 'g', coral: 'c', warn: 'w', brand: 'b', grey: 'n' };
export const compTone = (c) => ({ ok: 'g', bad: 'c', warn: 'w' }[c?.state] || 'n');

/**
 * build 396 -- the Sound chip/tile when the box's own volume is MUTED: amber "Sound muted" (never red, never a fault), green only when
 * not muted AND sound is healthy. `muted` is the live value (the same RTDB isMuted the Screens card's speaker button reads) when the page
 * has it, else the backend's. Only a healthy sound is overridden; a real fault or an off/unknown sound is never hidden by a mute.
 */
export function effectiveSound(comp, muted) {
  const c = comp || { state: 'unknown', word: 'unknown' };
  if (muted === true && c.state === 'ok') return { state: 'warn', word: 'muted' };
  if (muted === false && c.state === 'warn' && c.word === 'muted') return { state: 'ok', word: 'ok' };
  return c;
}
/** The live mute value to use: the page's real-time value when it is a boolean, else the backend's (boolean|null). */
export const resolveMuted = (live, backend) => (typeof live === 'boolean' ? live : (typeof backend === 'boolean' ? backend : null));
export const effectiveComponents = (comps, muted) => (comps ? { ...comps, sound: effectiveSound(comps.sound, muted) } : comps);
export const WEEK_TONE = { green: 'g', coral: 'c', grey: 'n' };

/** "15:22" in the device's own timezone (Latin digits, as in the approved prototype). */
export function clock(iso, tz) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz || undefined }).format(d);
  } catch (_e) {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d);
  }
}

/** "3 seconds" / "10 minutes" / "2 hours" through the plural-aware sh.unit.* keys. */
export function fmtDuration(t, seconds) {
  const s = Math.max(0, Math.round(Number(seconds)));
  if (!Number.isFinite(s)) return '';
  if (s < 60) return t('sh.unit.second', { count: Math.max(1, s) });
  if (s < 3600) return t('sh.unit.minute', { count: Math.max(1, Math.round(s / 60)) });
  return t('sh.unit.hour', { count: Math.max(1, Math.round(s / 3600)) });
}

/**
 * build 396 -- a connected duration in WORDS in the page language ("3 ساعات و 37 دقيقة" / "3 hours 37 minutes"), through the same
 * plural-aware sh.unit.* keys fmtDuration uses. Replaces the hard-coded "2d : 5h : 30m". Under a minute reads as `justNow`.
 */
export function formatUptime(t, totalMinutes, justNow) {
  const m = Math.max(0, Math.floor(Number(totalMinutes)));
  if (!Number.isFinite(m)) return '';
  const days = Math.floor(m / 1440); const hours = Math.floor((m % 1440) / 60); const mins = m % 60;
  const parts = [];
  if (days > 0) parts.push(t('sh.unit.day', { count: days }));
  if (hours > 0) parts.push(t('sh.unit.hour', { count: hours }));
  if (mins > 0) parts.push(t('sh.unit.minute', { count: mins }));
  if (!parts.length) return justNow || '';
  return parts.join(t('sh.uptime_join'));
}

const nonEmpty = (v) => v !== undefined && v !== null && v !== '';


const ACTIVITY = new Set(['screen_off', 'screen_on', 'campaign_changed', 'restart', 'redownload', 'app_opened', 'app_closed', 'paused', 'resumed', 'muted', 'unmuted']);
const START_REASONS = ['audio_repair', 'update', 'crash_relaunch', 'boot', 'process_restart'];
/** build 405: "... (the app restarted itself to fix the sound)" - only for a reason we can word; anything else leaves the sentence as it was. */
export function startReasonSuffix(t, sentence, reason) {
  if (!sentence || !START_REASONS.includes(reason)) return sentence;
  return `${sentence} — ${t(`sh.log.start_reason.${reason}`)}`;
}
const tidy = (str) => str.replace(/\s+([.,،])/g, '$1').replace(/\s{2,}/g, ' ').trim();

/**
 * Who did it and from where, in plain words. actor = {kind:'user'|'support'|'hours'|'schedule'|'device', name, via:'dashboard'|'remote'|null}.
 * An admin action arrives already redacted to kind 'support' (the customer sees "the support team"); a user without a
 * display name reads impersonally ("The screen was turned off from the remote."), never with a blank or a raw key.
 */
export function activitySentence(t, event, params) {
  const p = params || {};
  const a = p.actor || {};
  const name = nonEmpty(a.name) ? String(a.name) : '';
  const via = a.via === 'dashboard' || a.via === 'remote' ? t(`sh.via.${a.via}`) : '';
  const campaign = nonEmpty(p.campaign) ? String(p.campaign) : '';
  const base = event === 'campaign_changed' && !campaign ? 'campaign_changed_nameless' : event;
  let variant;
  if (a.kind === 'support') variant = 'support';
  else if (a.kind === 'hours') variant = 'hours';
  else if (a.kind === 'schedule') variant = 'schedule';
  else if (a.kind === 'device') variant = 'device';
  else if (a.kind === 'device_remote') variant = 'device_remote'; // build 396: the box's own IR remote (mute / unmute)
  else variant = name ? 'named' : 'anon';
  const key = `sh.log.${base}.${variant}`;
  const fallback = `sh.log.${base}.anon`;
  const exists = t(key, { defaultValue: '' });
  const text = exists ? t(key, { who: name, via, campaign }) : t(fallback, { who: name, via, campaign });
  return tidy(text);
}

/** Log line -> sentence. Handles the prayer / duration variants. Unknown keys render nothing (never a raw key). */
export function logSentence(t, entry, tz) {
  if (!entry || !entry.key) return '';
  const p = entry.params || {};
  const ev = String(entry.key).replace('sh.log.', '');
  if (ACTIVITY.has(ev)) return ev === 'app_opened' ? startReasonSuffix(t, activitySentence(t, ev, p), p.reason) : activitySentence(t, ev, p);
  const dur = nonEmpty(p.seconds) ? fmtDuration(t, p.seconds) : nonEmpty(p.minutes) ? fmtDuration(t, Number(p.minutes) * 60) : '';
  switch (entry.key) {
    case 'sh.log.prayer': {
      const prayer = p.prayer && ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'].includes(p.prayer) ? t(`sh.prayer.${p.prayer}`) : '';
      if (prayer && dur) return t('sh.log.prayer_named_timed', { prayer, dur });
      if (prayer) return t('sh.log.prayer_named', { prayer });
      if (dur) return t('sh.log.prayer_timed', { dur });
      return t('sh.log.prayer');
    }
    case 'sh.log.playback_stalled': return dur ? t('sh.log.playback_stalled', { dur }) : t('sh.log.playback_stalled_nodur');
    case 'sh.log.playback_recovered': return dur ? t('sh.log.playback_recovered', { dur }) : t('sh.log.playback_recovered_nodur');
    case 'sh.log.output_recovered': return dur ? t('sh.log.output_recovered', { dur }) : t('sh.log.output_recovered_nodur');
    case 'sh.log.output_problem': case 'sh.log.output_gave_up': case 'sh.log.sound_suspect': return t(entry.key);
    // build 399 -- what the device repaired by itself after a network fault
    case 'sh.log.resumed_after_network': case 'sh.log.wifi_reenabled': case 'sh.log.resumed_after_end': return t(entry.key);
    // build 405 -- the connected USB sound device is faulty (repairs suspended); a restart line says WHY the app started when the player told us
    case 'sh.log.external_fault': return t(entry.key);
    case 'sh.log.restarted': return startReasonSuffix(t, t('sh.log.restarted'), p.reason);
    // fix round 420 -- the schedule's start / end and what a person did to it: the backend sends the finished MSA sentence (params.text); nothing to translate here
    case 'sh.log.schedule_started': case 'sh.log.schedule_ended': case 'sh.log.schedule_held': case 'sh.log.schedule_resumed': case 'sh.log.schedule_ended_early':
    // settings round: a change of the screen's settings (who + where: the platform / the remote / the screen itself) arrives the same way
    case 'sh.log.settings_changed':
      return nonEmpty(p.text) ? String(p.text) : '';
    // build 404 -- a frozen item the player skipped (the item's name when it was sent)
    case 'sh.log.item_stalled': return nonEmpty(p.item) ? t('sh.log.item_stalled', { item: String(p.item) }) : t('sh.log.item_stalled_noname');
    case 'sh.log.command_channel_reconnected': return dur ? t('sh.log.command_channel_reconnected', { dur }) : t('sh.log.command_channel_reconnected_nodur');
    case 'sh.log.auto_restart': {
      const why = ['wifi_not_back', 'degraded_after_network'].includes(p.reason) ? p.reason : 'other';
      return t('sh.log.auto_restart', { why: t(`sh.log.auto_restart_why.${why}`) });
    }
    case 'sh.log.pipeline_exhausted': case 'sh.log.pipeline_recovered': case 'sh.log.playback_gave_up':
      return t(entry.key);
    case 'sh.log.sound_fixed': return dur ? t('sh.log.sound_fixed', { dur }) : t('sh.log.sound_fixed_nodur');
    case 'sh.log.internet_blip': return dur ? t('sh.log.internet_blip', { dur }) : t('sh.log.internet_blip_nodur');
    case 'sh.log.picture_fixed': return dur ? t('sh.log.picture_fixed', { dur }) : t('sh.log.picture_fixed_nodur');
    case 'sh.log.online_again':
    case 'sh.log.started': case 'sh.log.restarted': case 'sh.log.updated': case 'sh.log.sound_problem':
    case 'sh.log.offline': case 'sh.log.picture_problem': case 'sh.log.emergency_started': case 'sh.log.emergency_ended':
      return t(entry.key);
    default: return '';
  }
}

export function logSubSentence(t, entry) {
  const k = entry?.sub?.key;
  return k === 'sh.log.sub.restart_sound' ? t('sh.log.sub.restart_sound') : '';
}

/** Customer-visible log, newest first; drops anything we can't word (so a new backend key can never print raw). */
export function visibleLog(t, log, tz) {
  return (Array.isArray(log) ? log : [])
    .map((e) => ({ e, text: logSentence(t, e, tz) }))
    .filter((x) => x.text)
    .map(({ e, text }) => {
      const key = String(e.key || '');
      let tone = { green: 'g', coral: 'c', warn: 'w', grey: 'n' }[e.tone] || 'n';
      if (key === 'sh.log.screen_off' || key === 'sh.log.paused' || key === 'sh.log.muted' || key === 'sh.log.schedule_held') tone = 'w';
      if (key === 'sh.log.app_closed') tone = 'c';
      return { at: e.at, time: clock(e.at, tz), tone, quiet: tone === 'n', text, sub: logSubSentence(t, e) };
    });
}

const params = (p, tz) => {
  const o = { ...(p || {}) };
  if (nonEmpty(o.since)) o.time = clock(o.since, tz);
  if (nonEmpty(o.at)) o.time = clock(o.at, tz);
  return o;
};

/** hero title / card line from a summary-style key ('sh.working_clean', 'sh.needs_sound', ...). */
export function titleText(t, item, tz) {
  const key = item?.key || '';
  const p = params(item?.params, tz);
  if (key === 'sh.working_blips') return t('sh.working_blips');
  return t(key, p);
}
export function cardText(t, summary, tz) {
  const key = summary?.key || '';
  const p = params(summary?.params, tz);
  if (key === 'sh.working_blips') return t('sh.card_working_blips', { count: Number(p.count) || 0 });
  return t(key.replace('sh.', 'sh.card.'), p);
}
export function subText(t, item, tz, titleParams) {
  const key = item?.key || '';
  const merged = { ...(titleParams || {}), ...(item?.params || {}) };
  const p = params(merged, tz);
  if (key === 'sh.sub.working_blips') return t('sh.sub_working_blips', { count: Number(p.count) || 0 });
  if (key === 'sh.sub.paused_user') {
    const a = merged.actor || {};
    const via = a.via === 'dashboard' || a.via === 'remote' ? t(`sh.via.${a.via}`) : '';
    const time = clock(merged.at, tz);
    if (a.kind === 'support') return t('sh.sub.paused_user_support', { time });
    return tidy(t(nonEmpty(a.name) ? 'sh.sub.paused_user_named' : 'sh.sub.paused_user_anon', { who: a.name, via, time }));
  }
  if (key === 'sh.sub.standby_user') {
    const a = merged.actor || {};
    const via = a.via === 'dashboard' || a.via === 'remote' ? t(`sh.via.${a.via}`) : '';
    const time = clock(merged.at, tz);
    if (a.kind === 'support') return t('sh.sub.standby_user_support', { time });
    return tidy(t(nonEmpty(a.name) ? 'sh.sub.standby_user_named' : 'sh.sub.standby_user_anon', { who: a.name, via, time }));
  }
  if (key === 'sh.sub.standby_hours') {
    const hasBack = nonEmpty(merged.back_at);
    return t(hasBack ? 'sh.sub.standby_hours' : 'sh.sub.standby_hours_noback', { from: merged.from, to: merged.to, back: merged.back_at });
  }
  return t(key, p);
}

/**
 * build 402 -- the shared playback label rule on the health page: a screen that is WORKING/CHECKING but playing without sound / still loading says so
 * in its pill (amber), instead of "working normally". `detail` = customer.playback_detail from the server; absent (old backend) = unchanged.
 */
export function pillDetailKey(state, detail) {
  if ((state === 'working' || state === 'checking' || state === 'paused') && (detail === 'no_sound' || detail === 'loading' || detail === 'media_stalled' || detail === 'prayer' || detail === 'external_fault')) return detail;
  return null;
}

export function pillLabel(t, state, detail) {
  const d = pillDetailKey(state, detail);
  if (d) return t({ no_sound: 'sh.pill_no_sound', loading: 'sh.pill_loading', media_stalled: 'sh.pill_media_stalled', prayer: 'sh.pill_prayer', external_fault: 'sh.pill_external_fault' }[d]);
  return t({ working: 'sh.pill_working', needs_you: 'sh.pill_needs_you', offline: 'sh.pill_offline', limited: 'sh.pill_limited', standby: 'sh.pill_standby', paused: 'sh.pill_paused', checking: 'sh.pill_checking', app_closed: 'sh.pill_app_closed' }[state] || 'sh.pill_working');
}

export function seenText(t, seen, tz) {
  if (seen?.key === 'last_signal') return t('sh.seen_last_signal', { time: clock(seen?.params?.at, tz) });
  return t('sh.seen_online_now');
}

export function actionTexts(t, action, heroTitleKey) {
  const k = String(action?.title?.key || '').replace('sh.act.', '');
  const map = { nothing: 'nothing', restart: 'restart', check_device: 'check_device', limited: 'limited', generic: 'generic', turn_on: 'turn_on', resume: 'resume', auto_repair: 'auto_repair', checking: 'checking', restart_unit: 'restart_unit', hours: 'hours', start_app: 'start_app', device_restart: 'device_restart', power_cycle: 'power_cycle' };
  const id = map[k] || 'nothing';
  const body = id === 'restart' && heroTitleKey === 'sh.needs_sound' ? t('sh.act.restart.b_sound') : t(`sh.act.${id}.b`);
  return { title: t(`sh.act.${id}.t`), body };
}

export function componentWord(t, name, comp) {
  const w = comp?.word || (comp?.state === 'ok' ? 'ok' : 'unknown');
  return t(`sh.comp.${name}.${w}`, { defaultValue: t(`sh.comp.${name}.unknown`) });
}

/** What the card chips show: green -> just the name; otherwise "name: problem word". */
export function chipText(t, name, comp) {
  // the sound words that are whole sentences on a chip: "الصوت مكتوم" / "نتحقق من الصوت" / "الصوت لا يصل إلى السماعات"
  if (name === 'sound' && ['muted', 'suspect', 'output_stalled', 'external_fault'].includes(comp?.word)) return t(`sh.comp.sound.${comp.word}_chip`);
  return comp?.state === 'ok' ? t(`sh.${name}`) : `${t(`sh.${name}`)}: ${componentWord(t, name, comp)}`;
}

/** Fill {screen_name} {company} {branch} {status} {screen_id} {time}; unknown placeholders are left out, never shown raw. */
export function fillTemplate(template, vars) {
  return String(template || '').replace(/\{([a-z_]+)\}/g, (_m, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : ''));
}

/** Digits only, for wa.me. null when there is no usable number (the button must then be hidden). */
export function whatsappDigits(e164) {
  const d = String(e164 || '').replace(/\D/g, '');
  return d.length >= 7 && d.length <= 15 ? d : null;
}

export function whatsappLink(support, lang, vars) {
  const digits = whatsappDigits(support?.whatsapp);
  if (!digits) return null;
  const template = lang === 'ar' ? support?.template_ar : support?.template_en;
  const text = fillTemplate(template || '', vars);
  return { url: `https://wa.me/${digits}?text=${encodeURIComponent(text)}`, text };
}

/** 7-day strip labels. Last = "Today". Weekday names come from Intl in the page language. */
export function weekLabels(t, week, lang, tz) {
  const n = Array.isArray(week) ? week.length : 0;
  return (week || []).map((d, i) => {
    if (i === n - 1) return t('sh.today_word');
    try {
      return new Intl.DateTimeFormat(lang === 'ar' ? 'ar' : 'en', { weekday: lang === 'ar' ? 'long' : 'short', timeZone: 'UTC' }).format(new Date(`${d.date}T12:00:00Z`));
    } catch (_e) { return d.date; }
  });
}

/** build 422: the heading of the day log. today / yesterday / «ماذا حدث يوم الجمعة ٢٣ أكتوبر». `date` and `todayDate` are the device-local YYYY-MM-DD of the 7-day strip. */
export function dayTitle(t, lang, date, todayDate) {
  if (!date || date === todayDate) return t('sh.today_title');
  const diff = Math.round((Date.parse(`${todayDate}T12:00:00Z`) - Date.parse(`${date}T12:00:00Z`)) / 86400000);
  if (diff === 1) return t('sh.yesterday_title');
  return t('sh.day_title', { date: dayLabel(lang, date) });
}

/** «الجمعة ٢٣ أكتوبر» (weekday, day, month - no comma). */
export function dayLabel(lang, date) {
  try {
    const parts = new Intl.DateTimeFormat(lang === 'ar' ? 'ar-EG' : 'en', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).formatToParts(new Date(`${date}T12:00:00Z`));
    const get = (ty) => parts.find((p) => p.type === ty)?.value || '';
    return lang === 'ar' ? `${get('weekday')} ${get('day')} ${get('month')}` : `${get('weekday')} ${get('day')} ${get('month')}`;
  } catch (_e) { return date; }
}

/** Banner sub line: "Bar screen: no sound · Outdoor: offline". */
export function bannerDetail(t, screens) {
  const bad = screens.filter((s) => needsOwner(s.state));
  return bad.map((s) => {
    const comp = ['sound', 'picture', 'internet'].find((c) => s.components?.[c]?.state === 'bad');
    return `${s.name}: ${comp ? componentWord(t, comp, s.components[comp]) : t(s.state === 'offline' ? 'sh.pill_offline' : s.state === 'app_closed' ? 'sh.pill_app_closed' : 'sh.pill_needs_you')}`;
  }).join(' · ');
}
